import { useMemo, useState } from 'react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'
import { useNavigate } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { useJobAnalytics } from './hooks/useJobAnalytics'
import './JobAnalyticsScreen.css'

function formatHours(value) {
  return `${Number(value || 0).toFixed(1)}h`
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`
}

function formatInteger(value) {
  return Number(value || 0).toLocaleString('en-US')
}

function titleCase(value) {
  return String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (character) => character.toUpperCase())
}

function AnalyticsJobTable({ columns, rows, rowKey, renderRow, emptyMessage = 'No records match the current filters.', className = '' }) {
  return (
    <div className={`analytics-job-list-card ${className}`}>
      <div className="analytics-job-list-scroll">
        <table className="analytics-job-list-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row, index) => <tr key={rowKey(row, index)}>{renderRow(row, index)}</tr>) : (
              <tr><td className="analytics-job-list-empty" colSpan={columns.length}>{emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function JobEstimatedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-job-tooltip">
      <p className="analytics-job-tooltip__title">{label}</p>
      <div className="analytics-job-tooltip__row">
        <span className="analytics-job-tooltip__series"><span className="analytics-job-tooltip__dot analytics-job-tooltip__dot--estimated" />Estimated</span>
        <strong>{formatHours(item.estimatedHours)}</strong>
      </div>
      <div className="analytics-job-tooltip__row">
        <span className="analytics-job-tooltip__series"><span className="analytics-job-tooltip__dot analytics-job-tooltip__dot--logged" />Logged</span>
        <strong>{formatHours(item.loggedHours)}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

function JobDelayTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-job-tooltip">
      <p className="analytics-job-tooltip__title">{label}</p>
      <div className="analytics-job-tooltip__row">
        <span className="analytics-job-tooltip__series"><span className="analytics-job-tooltip__dot analytics-job-tooltip__dot--delay" />Average delay</span>
        <strong>{formatHours(item.avgDelayHours)} ({(Number(item.avgDelayHours || 0) / 24).toFixed(1)}d)</strong>
      </div>
      <div className="analytics-job-tooltip__row">
        <span>Delayed jobs</span>
        <strong>{formatInteger(item.delayedJobCount)}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

export default function JobAnalyticsScreen() {
  const { state, actions } = useJobAnalytics()
  const navigate = useNavigate()
  useBodyScrollLock(state.isDetailsOpen)
  const [delayMode, setDelayMode] = useState('daily')
  const [estimatedView, setEstimatedView] = useState('chart')
  const [delayView, setDelayView] = useState('chart')
  const insights = state.insights || {}
  const estimatedVsLogged = insights.estimatedVsLogged || {}
  const completionTrend = insights.completionTrend || {}
  const activeJobs = insights.activeJobs || {}
  const resourcePoolLoad = insights.resourcePoolLoad || {}
  const delayTrend = insights.delayTrend || {}

  const weeklySeries = Array.isArray(estimatedVsLogged.series) ? estimatedVsLogged.series : []
  const maxHours = Math.max(
    1,
    ...weeklySeries.map((item) => Math.max(Number(item.estimatedHours || 0), Number(item.loggedHours || 0))),
  )

  const delaySeries = useMemo(() => {
    if (delayMode === 'weekly') {
      return Array.isArray(delayTrend.weekly) ? delayTrend.weekly : []
    }
    return Array.isArray(delayTrend.daily) ? delayTrend.daily : []
  }, [delayMode, delayTrend.daily, delayTrend.weekly])

  const currentDelay = delaySeries.length ? Number(delaySeries[delaySeries.length - 1]?.avgDelayHours || 0) : 0
  const currentDelayLabel = delaySeries.length ? String(delaySeries[delaySeries.length - 1]?.label || '-') : '-'

  const topPools = Array.isArray(resourcePoolLoad.topPools) ? resourcePoolLoad.topPools : []
  const drillRows = Array.isArray(state.drilldown?.rows) ? state.drilldown.rows : []
  const drillKeys = drillRows.length ? Object.keys(drillRows[0]).filter((key) => !['id', 'projectId', 'userId'].includes(key)) : []

  return (
    <div className="w-full max-w-[1600px] px-8 py-8">
      <section className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="mb-2 font-headline text-5xl font-extrabold tracking-tight text-on-surface">Job Analytics</h2>
          <p className="max-w-md text-on-surface-variant">
            Real-time performance tracking and resource allocation intelligence for the Bridge Meridian network.
          </p>
        </div>
        <div className="grid grid-cols-2 items-end gap-4 rounded-lg bg-surface-container-low p-4 md:ml-auto md:grid-cols-4 xl:grid-cols-7">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Project</label>
            <AppDropdown value={state.projectId} onChange={(value) => actions.setProjectId(String(value))} options={state.projectOptions} placeholder="All Projects" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Resource Pool</label>
            <AppDropdown
              value={state.resourcePoolId}
              onChange={(value) => actions.setResourcePoolId(String(value))}
              options={state.resourcePoolOptions}
              placeholder="All RPs"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Date Range</label>
            <AppDropdown
              value={state.presetDays}
              onChange={(value) => actions.applyPreset(String(value))}
              options={state.presetOptions}
              placeholder="Last 30 Days"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Resource</label>
            <AppDropdown value={state.userId} onChange={(value) => actions.setUserId(String(value))} options={state.userOptions} placeholder="All Resources" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Status</label>
            <AppDropdown value={state.jobStatus} onChange={(value) => actions.setJobStatus(String(value))} options={state.statusOptions} placeholder="All Statuses" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Priority</label>
            <AppDropdown value={state.priority} onChange={(value) => actions.setPriority(String(value))} options={state.priorityOptions} placeholder="All Priorities" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Department</label>
            <AppDropdown value={state.departmentId} onChange={(value) => actions.setDepartmentId(String(value))} options={state.departmentOptions} placeholder="All Departments" />
          </div>
        </div>
      </section>

      {state.error ? <div className="analytics-job-status analytics-job-status-error mb-4">{state.error}</div> : null}
      {state.isLoading ? <div className="analytics-job-status mb-4">Loading job analytics...</div> : null}

      <div className="grid grid-cols-12 gap-6">
        <div className="group relative col-span-12 overflow-hidden rounded-lg bg-surface-container-lowest p-8 lg:col-span-7">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h3 className="font-headline text-2xl font-bold">Estimated vs Logged Hours</h3>
              <p className="text-sm text-on-surface-variant">Comparison of planned efficiency vs actual execution</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-primary-container" />
                <span className="text-xs font-bold uppercase tracking-tighter">Estimated</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: '#94a3b8' }} />
                <span className="text-xs font-bold uppercase tracking-tighter">Logged</span>
              </div>
            </div>
          </div>
          <div className="mb-4 flex items-center justify-between text-xs text-on-surface-variant">
            <span>X-axis: period · Y-axis: hours</span>
            <div className="flex rounded bg-surface-container-low p-1">
              {['chart', 'table'].map((view) => <button key={view} type="button" onClick={() => setEstimatedView(view)} className={`rounded px-2 py-1 font-bold capitalize ${estimatedView === view ? 'bg-white shadow-sm' : ''}`}>{view}</button>)}
            </div>
          </div>
          {estimatedView === 'chart' ? <div className="h-64 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 256 }}>
              <BarChart data={weeklySeries} barGap={4}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.14)" />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tickMargin={8} minTickGap={18} interval="preserveStartEnd" />
                <YAxis width={46} unit="h" domain={[0, Math.max(maxHours, 1)]} />
                <RechartsTooltip cursor={{ fill: 'rgba(0,71,171,0.05)' }} content={<JobEstimatedTooltip />} />
                <Bar dataKey="loggedHours" fill="#94a3b8" radius={[4, 4, 0, 0]}
                  onClick={(data) => {
                    const title = `Estimated vs Logged — ${data?.label || 'selected period'}`
                    actions.openDetails(title, 'job', data?.periodStart || null, data?.periodEnd || null)
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <Bar dataKey="estimatedHours" fill="var(--primary-container)" radius={[4, 4, 0, 0]}
                  onClick={(data) => {
                    const title = `Estimated vs Logged — ${data?.label || 'selected period'}`
                    actions.openDetails(title, 'job', data?.periodStart || null, data?.periodEnd || null)
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div> : <AnalyticsJobTable
            className="max-h-64"
            columns={['Period', 'Estimated (h)', 'Logged (h)', 'Variance (h)', 'Variance (%)']}
            rows={weeklySeries}
            rowKey={(item) => item.label}
            emptyMessage="No estimated or logged hours for the selected period."
            renderRow={(item) => {
              const variance = Number(item.loggedHours || 0) - Number(item.estimatedHours || 0)
              const variancePercent = Number(item.estimatedHours || 0) ? (variance / Number(item.estimatedHours)) * 100 : 0
              return <>
                <td className="analytics-job-list-primary">{item.label}</td>
                <td className="analytics-job-list-number">{formatHours(item.estimatedHours)}</td>
                <td className="analytics-job-list-number">{formatHours(item.loggedHours)}</td>
                <td className={`analytics-job-list-number ${variance > 0 ? 'analytics-job-list-positive' : variance < 0 ? 'analytics-job-list-negative' : ''}`}>{formatHours(variance)}</td>
                <td className={`analytics-job-list-number ${variancePercent > 0 ? 'analytics-job-list-positive' : variancePercent < 0 ? 'analytics-job-list-negative' : ''}`}>{formatPercent(variancePercent)}</td>
              </>
            }}
          />}
        </div>

        <div className="col-span-12 flex flex-col gap-6 lg:col-span-5">
          <button type="button" onClick={() => actions.openDetails('Completion Trend — jobs due')} className="relative flex-1 overflow-hidden rounded-lg bg-primary-gradient p-8 text-left text-white">
            <div className="relative z-10">
              <label className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">Completion Trend</label>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-headline text-5xl font-black">{formatPercent(completionTrend.currentPercent)}</span>
                <span className="text-sm font-semibold text-on-primary-container">
                  <span className="material-symbols-outlined text-sm">arrow_upward</span> {formatPercent(completionTrend.deltaPercent)}
                </span>
              </div>
              <p className="mt-4 text-sm font-light leading-relaxed text-primary-fixed">
                {completionTrend.narrative || 'System performance is exceeding quarterly benchmarks by 4.2%.'}
              </p>
              <p className="mt-3 text-xs font-semibold">{formatInteger(completionTrend.completedJobs)} completed / {formatInteger(completionTrend.activeJobs)} total due jobs · vs Previous Period</p>
              <p className="mt-2 text-xs opacity-90">On time {formatInteger(completionTrend.completedOnTime)} · Late {formatInteger(completionTrend.completedLate)} · In progress {formatInteger(completionTrend.inProgress)} · Overdue {formatInteger(completionTrend.overdue)}</p>
            </div>
          </button>
          <button type="button" onClick={() => actions.openDetails('Active Jobs')} className="flex-1 rounded-lg bg-surface-container-highest p-8 text-left">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-on-surface-variant">Active Jobs</label>
            <div className="mt-1 font-headline text-4xl font-bold text-primary">{formatInteger(activeJobs.count)}</div>
            <p className="mt-2 text-xs text-on-surface-variant">Open project, owner, status, and due-date details</p>
          </button>
        </div>

        <div className="col-span-12 rounded-lg bg-surface-container-low p-8 lg:col-span-4">
          <h3 className="mb-6 font-headline text-xl font-bold">Resource Pool Load</h3>
          <div className="space-y-6">
            {topPools.map((pool) => (
              <button type="button" onClick={() => actions.openDetails(`${pool.resourcePoolName} — resource allocation`)} key={`${pool.resourcePoolId || pool.resourcePoolName}`} className="block w-full text-left">
                <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                  <span>{pool.resourcePoolName}</span>
                  <span>{formatPercent(pool.utilizationPercent)}</span>
                </div>
                <p className="mb-2 text-[11px] text-on-surface-variant">Capacity {formatHours(pool.capacityHours)} · Allocated {formatHours(pool.plannedHours)} · Available {formatHours(pool.availableHours)} · {pool.loadClassification}</p>
                <AppTooltip
                  content={`Planned: ${formatHours(pool.plannedHours)} | Logged: ${formatHours(pool.loggedHours)} | Jobs: ${pool.jobCount || 0}`}
                >
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                    <div
                      className={`h-full rounded-full ${Number(pool.utilizationPercent || 0) >= 90 ? 'bg-error' : 'bg-primary'}`}
                      style={{ width: `${Math.max(4, Math.min(100, Number(pool.utilizationPercent || 0)))}%` }}
                    />
                  </div>
                </AppTooltip>
              </button>
            ))}
            {!topPools.length ? <p className="text-sm text-on-surface-variant">No resource pool load available.</p> : null}
          </div>
          <button type="button" className="mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary" onClick={() => actions.openDetails('Detailed Resource Distribution')}>
            View Detailed Distribution <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </div>

        <div className="col-span-12 flex min-h-[420px] flex-col rounded-lg bg-surface-container-lowest p-8 lg:col-span-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h3 className="font-headline text-2xl font-bold">Delay Trend Analysis</h3>
              <p className="text-sm text-on-surface-variant">Tracking delivery lag across active pipelines</p>
            </div>
            <div className="flex rounded-md bg-surface-container-low p-1">
              <button
                type="button"
                className={`px-3 py-1 text-xs font-bold ${delayMode === 'daily' ? 'rounded bg-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => setDelayMode('daily')}
              >
                Daily
              </button>
              <button
                type="button"
                className={`px-3 py-1 text-xs font-bold ${delayMode === 'weekly' ? 'rounded bg-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => setDelayMode('weekly')}
              >
                Weekly
              </button>
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between text-xs font-semibold text-on-surface-variant">
            {currentDelayLabel}: {formatHours(currentDelay)} Delay
            <div className="flex rounded bg-surface-container-low p-1">{['chart', 'table'].map((view) => <button key={view} type="button" onClick={() => setDelayView(view)} className={`rounded px-2 py-1 font-bold capitalize ${delayView === view ? 'bg-white shadow-sm' : ''}`}>{view}</button>)}</div>
          </div>
          <p className="mb-2 text-xs text-on-surface-variant">X-axis: due date/week · Y-axis: average schedule delay hours · includes delayed-job count (click for delayed job details)</p>
          {delayView === 'chart' ? <div className="mt-2 h-[260px] w-full shrink-0 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 260 }}>
              <AreaChart data={delaySeries}
                onClick={(chartData) => {
                  const point = chartData?.activePayload?.[0]?.payload
                  const title = `Delay Trend — ${point?.label || 'selected period'}`
                  actions.openDetails(title, 'job', point?.periodStart || null, point?.periodEnd || null)
                }}
                style={{ cursor: 'pointer' }}
              >
                <defs>
                  <linearGradient id="jobDelayGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="5%" stopColor="var(--primary-container)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="var(--primary-container)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.14)" />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tickMargin={8} minTickGap={20} interval="preserveStartEnd" />
                <YAxis width={54} unit="h" />
                <RechartsTooltip cursor={false} content={<JobDelayTooltip />} />
                <Area type="natural" dataKey="avgDelayHours" stroke="var(--primary-container)" fill="url(#jobDelayGradient)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div> : <AnalyticsJobTable
            className="max-h-[260px]"
            columns={['Due Date / Week', 'Delayed Jobs', 'Average Delay (hours)', 'Average Delay (days)']}
            rows={delaySeries}
            rowKey={(item) => item.label}
            emptyMessage="No delay records for the selected period."
            renderRow={(item) => <>
              <td className="analytics-job-list-primary">{item.label}</td>
              <td><span className={`analytics-job-list-count ${Number(item.delayedJobCount || 0) ? 'analytics-job-list-count--alert' : ''}`}>{formatInteger(item.delayedJobCount)}</span></td>
              <td className="analytics-job-list-number">{formatHours(item.avgDelayHours)}</td>
              <td className="analytics-job-list-number">{(Number(item.avgDelayHours || 0) / 24).toFixed(1)}d</td>
            </>}
          />}
        </div>

        <div className="col-span-12 mt-4 flex flex-col items-center justify-between gap-6 rounded-lg border border-white/40 bg-surface-container-low p-10 lg:flex-row">
          <div>
            <h4 className="font-headline text-lg font-bold">Export Insight Report</h4>
            <p className="max-w-sm text-sm text-on-surface-variant">
              Export the current filters and the underlying job drill-down data.
            </p>
          </div>
          <div className="flex gap-4">
            <button type="button" onClick={() => actions.openDetails('Export Job Analytics')} className="rounded-md border border-outline-variant/30 bg-surface-container-lowest px-6 py-3 text-sm font-bold text-on-surface">
              Review Data
            </button>
            <button type="button" onClick={actions.exportCurrentDrilldown} disabled={state.isExporting} className="rounded-md bg-primary-gradient px-8 py-3 text-sm font-bold text-white shadow-md disabled:opacity-60">
              {state.isExporting ? 'Exporting...' : 'Download CSV'}
            </button>
            <button type="button" onClick={() => actions.exportCurrentDrilldown('pdf')} disabled={state.isExporting} className="rounded-md border border-outline-variant/30 bg-surface-container-lowest px-6 py-3 text-sm font-bold text-on-surface disabled:opacity-60">
              Download PDF
            </button>
          </div>
        </div>
      </div>

      {state.isDetailsOpen ? (
        <div className="analytics-job-drawer-overlay" onClick={actions.closeDetails} role="presentation">
          <aside className="analytics-job-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-job-drawer-header">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Job Analytics / {state.drillLevel === 'timesheet' ? 'Timesheets' : 'Jobs'}</p>
                <h3 className="font-headline text-3xl font-bold text-primary">{state.drillTitle}</h3>
                {state.drillPeriodFrom && state.drillPeriodTo ? (
                  <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                    <span className="material-symbols-outlined text-sm">calendar_today</span>
                    {state.drillPeriodFrom} → {state.drillPeriodTo}
                  </p>
                ) : null}
                <p className="mt-2 text-sm text-on-surface-variant">Current filters remain applied. Select a job to open its record or view activity-level timesheets.</p>
              </div>
              <button type="button" className="material-symbols-outlined text-on-surface-variant" onClick={actions.closeDetails}>
                close
              </button>
            </div>
            <div className="analytics-job-drawer-content">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-on-surface-variant">
                <span>Job Analytics</span><span>/</span><span className="text-primary">{state.drillTitle}</span>
                <button type="button" className="ml-auto rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary" onClick={actions.closeDetails}>Back</button>
                <button type="button" className="rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary" onClick={() => {
                  if (state.drillLevel === 'job') actions.closeDetails()
                  else if (state.drillLevel === 'task') actions.openDetails('Job Activity', 'job', state.drillPeriodFrom, state.drillPeriodTo)
                  else actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)
                }}>Up One Level</button>
              </div>
              <div className="analytics-job-drawer-nav">
                <button
                  type="button"
                  className={`analytics-job-drawer-tab ${state.drillLevel === 'job' ? 'active' : ''}`}
                  onClick={() => actions.openDetails(state.drillTitle, 'job', state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Job Activity
                </button>
                <button
                  type="button"
                  className={`analytics-job-drawer-tab ${state.drillLevel === 'timesheet' ? 'active' : ''}`}
                  onClick={() => actions.openTimesheets(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Timesheet Activity
                </button>
                <button type="button" className={`analytics-job-drawer-tab ${state.drillLevel === 'task' ? 'active' : ''}`} onClick={() => actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)}>
                  Task Activity
                </button>
                <button
                  type="button"
                  className="analytics-job-drawer-tab"
                  onClick={actions.resetFilters}
                >
                  Reset Drilldown
                </button>
                <button
                  type="button"
                  className="ml-auto rounded bg-primary px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
                  onClick={actions.exportCurrentDrilldown}
                  disabled={state.isExporting}
                >
                  {state.isExporting ? 'Exporting...' : 'Export CSV'}
                </button>
                <button type="button" className="rounded border border-primary px-3 py-2 text-xs font-bold text-primary disabled:opacity-60" onClick={() => actions.exportCurrentDrilldown('pdf')} disabled={state.isExporting}>Export PDF</button>
              </div>
              {state.drilldownError ? <p className="mb-4 text-sm text-error">{state.drilldownError}</p> : null}
              {state.isLoadingDrilldown ? <p className="mb-4 text-sm text-on-surface-variant">Loading drill-down data...</p> : null}
              <AnalyticsJobTable
                className="analytics-job-list-card--drawer"
                columns={[...drillKeys.map(titleCase), ...(['job', 'task', 'timesheet'].includes(state.drillLevel) ? ['Action'] : [])]}
                rows={drillRows}
                rowKey={(row) => `${state.drillLevel}-${row.id}`}
                emptyMessage={state.isLoadingDrilldown ? 'Loading details...' : 'No drill-down records match the current filters.'}
                renderRow={(row) => <>
                  {drillKeys.map((key) => {
                    const value = row[key]
                    if (key === 'status' || key === 'priority' || key === 'approvalStatus' || key === 'loadClassification') {
                      return <td key={key}><span className="analytics-job-list-badge">{value || '-'}</span></td>
                    }
                    return <td key={key} className={typeof value === 'number' ? 'analytics-job-list-number' : key === 'job' || key === 'project' ? 'analytics-job-list-primary' : ''}>{typeof value === 'number' ? value.toLocaleString('en-US') : value || '-'}</td>
                  })}
                  {state.drillLevel === 'job' ? <td><button type="button" className="analytics-job-list-action" onClick={() => navigate(`/nerve/job/entry?id=${row.id}`)}>View Job <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                  {state.drillLevel === 'task' ? <td><button type="button" className="analytics-job-list-action" onClick={() => navigate(`/nerve/task/entry?id=${row.id}`)}>View Task <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                  {state.drillLevel === 'timesheet' ? <td><button type="button" className="analytics-job-list-action" onClick={() => navigate(`/nerve/timesheet/entry?id=${row.id}&mode=view`)}>View Timesheet <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                </>}
              />
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  )
}
